<template>
    <page :tabs="tabs" :busy="busy">
        <grid ref="grid"
              :columnDefs="columnDefs"
              :rowData="rowData"
              grid-theme="ag-theme-alpine-report"
        >
              <date-range-picker
                  :range-options="predefinedRanges"
                  :value="dateRange"
                  :date-format="userUnicodeDateFormat"
                  @input="handleDaterangeChange"
              ></date-range-picker>
              <responsive-button
                  :href="exportExcelHref"
                  :icon="['fas', 'file-excel']"
                  variant="primary"
              />
              <exportToPDFButton
                :href="exportPdfHref"
                :pdfCacheKey="pdfCacheKey"
                :pageOrientation="pageOrientation"
              />
              <responsive-dropdown
                  :icon="['fas', 'file']"
                  :items="orientationFileOption"
                  variant="primary"
                  >{{ orientationFileLabel }}
              </responsive-dropdown>
              <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
            />
        </grid>
    </page>
</template>

<script>
    import API from '../../api';
    import grid from '../generic/grid/grid.vue';
    import page from '../generic/page/page.vue';
    import format from 'date-fns/format';
    import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
    import { INVOICE_CHANGES_LIST_ENDPOINT } from '../../endpoints';
    import DateRangePicker, {
        FUTURE,
        TODAY,
        YESTERDAY,
        THIS_WEEK,
        LAST_WEEK,
        THIS_MONTH,
        LAST_MONTH,
        THIS_YEAR,
        LAST_YEAR,
        ALL_DATES,
        nameToRange,
    } from '../generic/date-range-picker/date-range-picker.vue';
    import { mapGetters } from 'vuex';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import ReportSettingsModal, {
        DATE_PERIODS_INPUT,
    } from './report-settings/report-settings-modal.vue';
import { defineAsyncComponent } from 'vue';

    const exportToPDFButton = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-export-to-pdf-button" */
            '../generic/export-to-pdf-button/export-to-pdf-button.vue'
        ))

    export default {
        name: 'InvoiceChanges',
        components: {
            page,
            grid,
            DateRangePicker,
            ResponsiveButton,
            exportToPDFButton,
            ResponsiveDropdown,
            ReportSettingsModal,
        },
        data() {
            return {
                busy: null,
                dateRange: nameToRange(THIS_MONTH),
                dateRequestFormat: 'yyyy-MM-dd',
                to_date: null,
                landscapeOrientationFileLabel: 'Landscape',
                portraitOrientationFileLabel: 'Portrait',
                orientationFileLabel: 'Landscape',
                pageOrientation: 'landscape',
                predefinedRanges: [
                    FUTURE,
                    TODAY,
                    YESTERDAY,
                    THIS_WEEK,
                    LAST_WEEK,
                    THIS_MONTH,
                    LAST_MONTH,
                    THIS_YEAR,
                    LAST_YEAR,
                    ALL_DATES,
                ],
                rowData: [],
                from_date: null,
                tabs: [
                    {
                        title: 'Invoice Changes Report',
                        active: true,
                    },
                ],
                pdfCacheKey: null,
                reportSettingsModalId: 'report-settings-modal',

            };
        },
        computed: {
            ...mapGetters('header', [
                'userUnicodeDateFormat',
                'userCurrencySymbol',
            ]),
            columnDefs() {
                return [
                    {
                        field: 'happened_at',
                        headerName: 'Timestamp',
                        type: 'dateTimeColumn',
                        minWidth: 150,
                    },
                    {
                        field: 'invoice',
                        headerName: 'Invoice #',
                        minWidth: 90,
                        maxWidth: 100,
                        cellRenderer: ({ data }) => {
                            return `<a href="/app/reservation/edit/invoice/${data.invoice}">${data.invoice}</a>`;
                        },
                    },
                    {
                        field: 'short_name',
                        headerName: 'Guest Name',
                        minWidth: 150,
                    },
                    {
                        field: 'properties',
                        headerName: 'Property(ies)',
                        minWidth: 250,
                    },
                    {
                        field: 'details',
                        headerName: 'Details',
                        minWidth: 500,
                    },
                    {
                        field: 'user',
                        headerName: 'User',
                        minWidth: 220,
                    },
                    {
                        field: 'ip_address',
                        headerName: 'IP Address',
                        minWidth: 120,
                    },
                ];
            },

            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            exportPdfHref() {
                return `/app/reports/invoice-changes/pdf/?from_date=${this.startDateRequest}&to_date=${this.endDateRequest}`;
            },
            exportExcelHref() {
                return `/app/reports/invoice-changes/excel/?from_date=${this.startDateRequest}&to_date=${this.endDateRequest}`;
            },
            orientationFileOption() {
                return [
                    {
                        label: this.landscapeOrientationFileLabel,
                        click: () => {
                            this.pageOrientation = 'landscape';
                            this.orientationFileLabel = this.landscapeOrientationFileLabel;
                        },
                    },
                    {
                        label: this.portraitOrientationFileLabel,
                        click: () => {
                            this.pageOrientation = 'portrait';
                            this.orientationFileLabel = this.portraitOrientationFileLabel;
                        },
                    },
                ];
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            reportSettings() {
                return {
                    name: 'Invoice Changes',
                    report_sysname: 'INVOICE_CHANGES',
                    settings: [
                        {
                            type: DATE_PERIODS_INPUT,
                            value: 'this_month',
                            predefinedRanges: this.predefinedRanges,
                            label: 'Period',
                            name: 'from_date',
                        },
                    ],
                };
            },
        },
        mounted() {
            this.fetchData();
        },
        methods: {
            async fetchData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(
                        INVOICE_CHANGES_LIST_ENDPOINT(),
                        {
                            params: {
                                from_date: this.startDateRequest,
                                to_date: this.endDateRequest,
                            },
                        }
                    );
                    this.rowData = data.items;
                    this.pdfCacheKey = data.pdf_cache_key
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data' });
                } finally {
                    this.busy = false;
                }
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.fetchData();
            },
        },
    };
</script>
